import React, {
    useState,
    useEffect,
    useCallback
} from "react";

import {
    Button,
    Popover,
    Popconfirm,
    message,
    Spin,
    Pagination
} from "antd";

import {
    DeleteOutlined,
    InfoCircleOutlined,
    ReloadOutlined
} from "@ant-design/icons";

import Layout from "../../Layout";
import SearchBox from "./SearchBox";
import AdvancedSearch from "./AdvancedSearch";
import UploadModal from "./UploadModal";
import { Link } from "react-router-dom";

import { getBeOrderList } from "../../api/Order";

const formatMoney = (
    n: number | string
) => {
    const num =
        typeof n === "string"
            ? parseFloat(n)
            : n;

    return (
        num?.toLocaleString("vi-VN") ??
        "0"
    );
};

const formatDateTime = (
    dateStr: string
) => {
    if (!dateStr) {
        return {
            date: "—",
            time: "—"
        };
    }

    const d = new Date(dateStr);

    return {
        date: d.toLocaleDateString(
            "vi-VN"
        ),

        time: d.toLocaleTimeString(
            "vi-VN",
            {
                hour: "2-digit",
                minute: "2-digit"
            }
        )
    };
};

export default function TableCRM() {
    const [showAdvanced, setShowAdvanced] =
        useState(false);

    const [openUpload, setOpenUpload] =
        useState(false);

    const [data, setData] = useState<
        any[]
    >([]);

    const [loading, setLoading] =
        useState(false);

    /* =========================
       SEARCH
    ========================= */

    const [search, setSearch] =
        useState("");

    /* =========================
       FILTER
    ========================= */

    const [status, setStatus] =
        useState("");

    const [fromDate, setFromDate] =
        useState("");

    const [toDate, setToDate] =
        useState("");

    /* =========================
       PAGINATION
    ========================= */

    const [page, setPage] =
        useState(1);

    const [total, setTotal] =
        useState(0);

    /* =========================
       FETCH ORDERS
    ========================= */

    const fetchOrders =
        useCallback(async () => {
            setLoading(true);

            try {
                const result =
                    await getBeOrderList(
                        page,
                        20,

                        search,

                        fromDate,
                        toDate,

                        undefined,

                        status
                    );

                if (
                    result?.status ===
                        true &&
                    Array.isArray(
                        result.data?.items
                    )
                ) {
                    setData(
                        result.data.items
                    );

                    setTotal(
                        result.data.total
                    );

           
                }
            } catch (error) {
                console.error(error);

                message.error(
                    "Không thể tải danh sách đơn hàng"
                );
            } finally {
                setLoading(false);
            }
        }, [
            page,
            search,
            fromDate,
            toDate,
            status
        ]);

    useEffect(() => {
        fetchOrders();
    }, [
        page,
        status,
        fromDate,
        toDate
    ]);

    /* =========================
       DELETE
    ========================= */

    const handleDelete = (
        record: any
    ) => {
        console.log("Xóa:", record);

        message.success(
            "Đã xóa (demo)"
        );
    };

    return (
        <Layout>
            <div className="space-y-4 mb-4">
                {/* HEADER */}

                <div className="flex justify-between items-center">
                    <h2 className="text-lg font-semibold text-000000e0">
                        Danh sách bán hàng
                    </h2>

                    <div className="flex gap-2">
                        <Button
                            onClick={() =>
                                setOpenUpload(
                                    true
                                )
                            }
                        >
                            Nạp danh sách
                        </Button>

                        <UploadModal
                            open={
                                openUpload
                            }
                            onClose={() =>
                                setOpenUpload(
                                    false
                                )
                            }
                            onSuccess={
                                fetchOrders
                            }
                        />

                        <Button
                            onClick={
                                fetchOrders
                            }
                            icon={
                                <ReloadOutlined />
                            }
                        >
                            Refresh
                        </Button>
                    </div>
                </div>

                {/* TOOLBAR */}

                <div className="flex gap-3 flex-wrap items-center">
                <SearchBox
                    value={search}
                       onChange={(value) => {
                      setSearch(value);
              }}
                    onSearch={() => {
                       setPage(1);

                       fetchOrders();
                 }}
                     />

                    <Button
                        className="bg-[#3e91ab] text-white border-none"
                        onClick={() =>
                            setShowAdvanced(
                                !showAdvanced
                            )
                        }
                    >
                        Tìm kiếm nâng cao
                    </Button>
                </div>

                {/* ADVANCED SEARCH */}

                {showAdvanced && (
                    <AdvancedSearch
                        status={status}
                        setStatus={
                            setStatus
                        }
                        fromDate={
                            fromDate
                        }
                        setFromDate={
                            setFromDate
                        }
                        toDate={toDate}
                        setToDate={
                            setToDate
                        }
                        onFilter={() => {
                            setPage(1);
                        }}
                    />
                )}
            </div>

            {/* TABLE */}

            <Spin spinning={loading}>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm border border-gray-200 dark:border-gray-700 table-order-list">
                        <thead className="bg-gray-50 dark:bg-[#1f1f1f] text-gray-600 dark:text-gray-300 sticky top-0">
                            <tr>
                                <th>
                                    Mã đơn hàng
                                </th>

                                <th>
                                    Ngày giờ
                                </th>

                                <th className="hidden">
                                    Mã khách hàng
                                </th>

                                <th>
                                    Tên khách hàng
                                </th>

                                <th>
                                    Email
                                </th>

                                <th>
                                    Số điện thoại
                                </th>

                                <th>
                                    Địa chỉ
                                </th>

                                <th>
                                    Giá ĐH/TLT
                                </th>

                                <th>
                                    CK ĐH/TLT
                                </th>

                                <th>
                                    Thành tiền
                                </th>

                                <th>
                                    Đã thanh toán
                                </th>

                                <th>
                                    Công nợ
                                </th>

                                <th>
                                    Commission
                                </th>

                                <th>
                                    Trạng thái
                                </th>

                                <th>
                                    Loại đơn
                                </th>

                                <th>
                                    Chi nhánh
                                </th>

                                <th>
                                    Người tạo
                                </th>

                                <th>
                                    Ghi chú
                                </th>

                                <th></th>
                            </tr>
                        </thead>

                        <tbody className="bg-white dark:bg-[#141414]">
                            {data.length ===
                            0 ? (
                                <tr>
                                    <td
                                        colSpan={
                                            19
                                        }
                                        className="text-center py-12 text-gray-400"
                                    >
                                        Không
                                        có dữ
                                        liệu
                                    </td>
                                </tr>
                            ) : (
                                data.map(
                                    (
                                        item: any
                                    ) => {
                                        const {
                                            date,
                                            time
                                        } =
                                            formatDateTime(
                                                item.order_date
                                            );

                                        return (
                                            <tr
                                                key={
                                                    item.id
                                                }
                                                className="border-t hover:bg-gray-50 dark:hover:bg-[#1f1f1f]"
                                            >
                                                {/* ORDER CODE */}

                                                <td className="font-medium whitespace-nowrap">
                                                    <Link
                                                        to={`/order-detail?id=${item.id}`}
                                                        className="text-blue-600 hover:underline"
                                                    >
                                                        {item.order_code ||
                                                            "—"}
                                                    </Link>
                                                </td>

                                                {/* DATE */}

                                                <td className="whitespace-nowrap">
                                                    <div>
                                                        {
                                                            date
                                                        }
                                                    </div>

                                                    <div className="text-xs text-gray-400">
                                                        {
                                                            time
                                                        }
                                                    </div>
                                                </td>

                                                {/* CUSTOMER CODE */}

                                                <td  className="hidden">
                                                    {item.customer_code ||
                                                        "—"}
                                                </td> 

                                                {/* CUSTOMER NAME */}

                                                <td className="text-blue-600 font-medium min-w-[220px] break-words">
                                                    {item.customer_name ||
                                                        "—"}
                                                </td>

                                                {/* EMAIL */}

                                                <td>
                                                    {item.customer_email ||
                                                        "—"}
                                                </td>

                                                {/* PHONE */}

                                                <td>
                                                    {item.customer_phone ||
                                                        "—"}
                                                </td>

                                                {/* ADDRESS */}

                                                <td
                                                    className="max-w-[200px] truncate"
                                                    title={
                                                        item.customer_address
                                                    }
                                                >
                                                    {item.customer_address ||
                                                        "—"}
                                                </td>

                                                {/* TOTAL */}

                                                <td className="text-right">
                                                    {formatMoney(
                                                        item.total_amount ||
                                                            0
                                                    )}
                                                </td>

                                                {/* DISCOUNT */}

                                                <td className="text-right text-red-500">
                                                    {formatMoney(
                                                        item.discount_amount ||
                                                            0
                                                    )}
                                                </td>

                                                {/* FINAL */}

                                                <td className="text-right font-semibold text-green-600">
                                                    {formatMoney(
                                                        item.final_amount ||
                                                            0
                                                    )}
                                                </td>

                                                {/* PAID */}

                                                <td className="text-right text-blue-600">
                                                    {formatMoney(
                                                        item.paid_amount ||
                                                            0
                                                    )}
                                                </td>

                                                {/* DEBT */}

                                                <td className="text-right text-orange-600">
                                                    {formatMoney(
                                                        item.debt_amount ||
                                                            0
                                                    )}
                                                </td>

                                                {/* COMMISSION */}

                                                <td className="text-right">
                                                    {formatMoney(
                                                        item.commission ||
                                                            0
                                                    )}
                                                </td>

                                                {/* STATUS */}

                                                <td>
                                                    <span className="text-yellow-500">
                                                        ●{" "}
                                                        {item.status ||
                                                            "—"}
                                                    </span>
                                                </td>

                                                {/* ORDER TYPE */}

                                                <td>
                                                    {item.order_type ||
                                                        "—"}
                                                </td>

                                                {/* BRANCH */}

                                                <td className="whitespace-nowrap">
                                                    {item.branch_name ||
                                                        "—"}
                                                </td>

                                                {/* CREATED BY */}

                                                <td className="whitespace-nowrap">
                                                    {item.created_by_name ||
                                                        "—"}
                                                </td>

                                                {/* NOTE */}

                                                <td
                                                    className="max-w-[300px] truncate"
                                                    title={
                                                        item.note
                                                    }
                                                >
                                                    {item.note ||
                                                        "—"}
                                                </td>

                                                {/* ACTION */}

                                                <td className="text-right">
                                                    <div className="flex gap-2 justify-end">
                                                        <Popover
                                                            placement="left"
                                                            trigger="hover"
                                                            content={
                                                                <div className="text-sm max-w-md space-y-1">
                                                                    <div>
                                                                        <strong>
                                                                            Mã
                                                                            đơn:
                                                                        </strong>{" "}
                                                                        {
                                                                            item.order_code
                                                                        }
                                                                    </div>

                                                                    <div>
                                                                        <strong>
                                                                            Khách
                                                                            hàng:
                                                                        </strong>{" "}
                                                                        {
                                                                            item.customer_name
                                                                        }
                                                                    </div>

                                                                    <div>
                                                                        <strong>
                                                                            SĐT:
                                                                        </strong>{" "}
                                                                        {
                                                                            item.customer_phone
                                                                        }
                                                                    </div>

                                                                    <div>
                                                                        <strong>
                                                                            Thành
                                                                            tiền:
                                                                        </strong>{" "}
                                                                        {formatMoney(
                                                                            item.final_amount ||
                                                                                0
                                                                        )}
                                                                    </div>

                                                                    <div>
                                                                        <strong>
                                                                            Trạng
                                                                            thái:
                                                                        </strong>{" "}
                                                                        {
                                                                            item.status
                                                                        }
                                                                    </div>

                                                                    <div>
                                                                        <strong>
                                                                            Ghi
                                                                            chú:
                                                                        </strong>{" "}
                                                                        {item.note ||
                                                                            "—"}
                                                                    </div>
                                                                </div>
                                                            }
                                                        >
                                                            <InfoCircleOutlined className="cursor-pointer text-blue-500" />
                                                        </Popover>

                                                        <Popconfirm
                                                            title="Xóa đơn hàng?"
                                                            onConfirm={() =>
                                                                handleDelete(
                                                                    item
                                                                )
                                                            }
                                                        >
                                                            <DeleteOutlined className="cursor-pointer text-red-500" />
                                                        </Popconfirm>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    }
                                )
                            )}
                        </tbody>
                    </table>
                </div>

                {/* PAGINATION */}

                <div className="flex justify-end mt-4">
                    <Pagination
                    current={page}

                       total={total}

                         pageSize={20}

                        showSizeChanger={false}

                        onChange={(p) => {
                         setPage(p);
                        }}

                         showTotal={(total) =>
                         `Tổng ${total} đơn`
                         }
                    />
                </div>
            </Spin>
        </Layout>
    );
}